import { api } from '@/services/api'
import { capture, trackMeta } from '@/services/analytics'
import type { AttributionRequest, CheckoutResponse } from '@/types/api'

export const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
export const campaignParams = [
  'campaign_id', 'adset_id', 'ad_id', 'placement', 'site_source_name',
  'sub_id_1', 'sub_id_2', 'sub_id_3', 'sub_id_4', 'sub_id_5', 'keitaro_subid', 'click_id',
] as const

const keitaroSubidPattern = /^[A-Za-z0-9_.-]{1,100}$/

const attributionKey = 'uig_attribution'
const syncedKey = 'uig_attribution_synced'
const visitKey = 'uig_visit_sent'
const pendingCheckoutKey = 'uig_pending_checkout'
const fbcMaxAgeSeconds = 90 * 24 * 60 * 60
const attributionMaxAgeMs = fbcMaxAgeSeconds * 1000
const encodedSequence = /%[0-9a-f]{2}/i
const unresolvedMacro = /^\{+[^{}]*\}+$/

type StoredAttribution = Omit<AttributionRequest, 'fbc' | 'fbp'>
type Touch = Pick<StoredAttribution, 'landing_path' | 'fbclid' | 'params' | typeof utmParams[number]>

interface PendingCheckout {
  checkout_session_id: string
  purchase_event_names: string[]
  value: number
  currency: string
}

function read<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function write(storage: Storage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    return
  }
}

export function cookie(name: string): string | undefined {
  const prefix = `${name}=`
  return document.cookie.split('; ').find((part) => part.startsWith(prefix))?.slice(prefix.length)
}

export function metaCookies(): { fbc?: string, fbp?: string } {
  return { fbc: cookie('_fbc'), fbp: cookie('_fbp') }
}

function rememberClick(fbclid: string): void {
  if (cookie('_fbc')?.endsWith(`.${fbclid}`)) return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; Max-Age=${fbcMaxAgeSeconds}; Path=/; SameSite=Lax${secure}`
}

export function storedAttribution(now = Date.now()): StoredAttribution | null {
  const stored = read<StoredAttribution>(localStorage, attributionKey)
  const visitedAt = Date.parse(stored?.first_visit_at ?? '')
  return stored && now - visitedAt <= attributionMaxAgeMs ? stored : null
}

function queryValue(query: URLSearchParams, name: string): string | undefined {
  let value = query.get(name)?.trim()
  for (let pass = 0; value && pass < 2 && encodedSequence.test(value); pass += 1) {
    try {
      value = decodeURIComponent(value).trim()
    } catch {
      break
    }
  }
  if (!value || unresolvedMacro.test(value)) return undefined
  return value.slice(0, 500)
}

function readTouch(location: Location): Touch {
  const query = new URLSearchParams(location.search)
  const params: Record<string, string> = {}
  for (const name of campaignParams) {
    const value = queryValue(query, name)
    if (value) params[name] = value
  }
  const touch: Touch = {
    landing_path: `${location.pathname}${location.search}`.slice(0, 500),
    fbclid: queryValue(query, 'fbclid'),
    params,
  }
  for (const name of utmParams) {
    const value = queryValue(query, name)
    if (value) touch[name] = value
  }
  return touch
}

function isAdTouch(touch: Touch): boolean {
  return Boolean(touch.fbclid || utmParams.some((name) => touch[name]) || Object.keys(touch.params ?? {}).length)
}

export function captureAttribution(location: Location = window.location): StoredAttribution {
  const touch = readTouch(location)
  if (touch.fbclid) rememberClick(touch.fbclid)
  const existing = storedAttribution()
  if (existing) return existing
  const keitaroSubid = touch.params?.keitaro_subid ?? ''
  const attribution: StoredAttribution = {
    ...touch,
    first_visit_at: new Date().toISOString(),
    referrer: document.referrer.slice(0, 500) || undefined,
    keitaro_subid: keitaroSubidPattern.test(keitaroSubid) ? keitaroSubid : undefined,
  }
  write(localStorage, attributionKey, attribution)
  return attribution
}

export function trackVisit(location: Location = window.location): void {
  const touch = readTouch(location)
  const adTouch = isAdTouch(touch)
  if (!adTouch && read<boolean>(sessionStorage, visitKey)) return
  write(sessionStorage, visitKey, true)
  const campaign = Object.fromEntries(utmParams.flatMap((name) => touch[name] ? [[name, touch[name]]] : []))
  capture('visit', {
    landing_path: touch.landing_path,
    ad_touch: adTouch,
    ...(touch.fbclid ? { fbclid: touch.fbclid } : {}),
    ...campaign,
    ...touch.params,
  })
}

export async function syncAttribution(userId: string): Promise<void> {
  const synced = read<string[]>(localStorage, syncedKey) ?? []
  if (synced.includes(userId)) return
  try {
    const response = await api.recordAttribution({ ...(storedAttribution() ?? {}), ...metaCookies() })
    write(localStorage, syncedKey, [...synced, userId])
    if (response.signup) trackMeta(response.event_name, response.event_id)
  } catch {
    return
  }
}

export function trackCheckoutStarted(productId: string, checkout: CheckoutResponse): void {
  capture('checkout_started', {
    product_id: productId,
    value: checkout.value,
    currency: checkout.currency,
  })
  if (!checkout.checkout_session_id || !checkout.purchase_event_names?.length) return
  trackMeta('InitiateCheckout', `checkout:${checkout.checkout_session_id}`, {
    value: checkout.value,
    currency: checkout.currency,
    content_ids: [productId],
    content_type: 'product',
  })
  write(localStorage, pendingCheckoutKey, {
    checkout_session_id: checkout.checkout_session_id,
    purchase_event_names: checkout.purchase_event_names,
    value: checkout.value,
    currency: checkout.currency,
  })
}

export function trackCheckoutReturn(sessionId: string, purchaseEventId = `checkout:${sessionId}`): void {
  const pending = read<PendingCheckout>(localStorage, pendingCheckoutKey)
  if (!pending || pending.checkout_session_id !== sessionId) return
  try {
    localStorage.removeItem(pendingCheckoutKey)
  } catch {
    return
  }
  for (const eventName of pending.purchase_event_names ?? []) {
    trackMeta(eventName, purchaseEventId, {
      value: pending.value,
      currency: pending.currency,
    })
  }
}
